'use client';

/**
 * LegalModalProvider — app-wide context that owns the single
 * LegalModal instance + the open/close API. Mounted once at the root
 * layout so every LegalLink anywhere in the tree can pop the same
 * modal. See LegalModal.tsx for component contract.
 *
 * Track 3.3 (2026-05-13).
 */

import { createContext, useCallback, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { LegalModal, type LegalSlug } from './LegalModal';

interface LegalModalApi {
  open: (slug: LegalSlug) => void;
  close: () => void;
}

const LegalModalContext = createContext<LegalModalApi | null>(null);

export function LegalModalProvider({ children }: { children: ReactNode }) {
  const [slug, setSlug] = useState<LegalSlug | null>(null);
  const open = useCallback((s: LegalSlug) => setSlug(s), []);
  const close = useCallback(() => setSlug(null), []);
  return (
    <LegalModalContext.Provider value={{ open, close }}>
      {children}
      <LegalModal slug={slug} onClose={close} />
    </LegalModalContext.Provider>
  );
}

export function useLegalModal(): LegalModalApi {
  const ctx = useContext(LegalModalContext);
  if (!ctx) {
    throw new Error('useLegalModal must be used inside <LegalModalProvider>');
  }
  return ctx;
}
